'use client';

import { useState } from 'react';
import Markdown from 'react-markdown';

import rehypeExtLinks from 'rehype-external-links';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { useTranslations } from 'use-intl';

import {
  useProjectResponseModeContext,
  useRealtimeVoiceMatch,
  useSetIframeUrl,
} from '@/shared-survey';
import { type Messages, rehypeIndent } from '@/shared-ui';
import { QuestionType } from '@/survey-graphql';

import { ProjectResponseAssetItem } from './project-response-asset-item';
import ProjectResponseMessageRenderer from './project-response-message-renderer';
import { ensureTripleBackticks } from './project-response-message-renderers.helpers';
import type {
  ProjectResponseComponentProps,
  RendererOption,
} from './project-response-renderer.type';
import { reorderOptionsForMultipleChoice } from './project-usability-handsfree-popup-message-renderer.helpers';
import HandsFreePopupSendButton from './project-usability-handsfree-popup-send-button';

const SKIP_SIGNAL = 'skip' as string;

type ChatMessage = Messages[number];

function getQuestionTypeForVoiceMatch(type: RendererOption) {
  switch (type) {
    case 'multipleChoice':
      return QuestionType.MultipleChoice;
    case 'rating':
      return QuestionType.Rating;
    default:
      return QuestionType.OpenEnded;
  }
}

interface Props {
  type: RendererOption;
  text: string;
  options?: ChatMessage['options'];
  optionRenderType?: ChatMessage['optionRenderType'] | null;
  assets?: ChatMessage['assets'];
  contentUrl?: string | null;
  /** When set with MC/Rating/Ranking, options are clickable and submit via this callback. */
  onSubmitContent?: (content: string) => void;
  isSubmitting?: boolean;
  awaitingTaskCompletion?: boolean;
  maxSelections?: number | null;
  isConversationOver?: boolean;
  /** Controlled selection value. When provided with onChange, renderer uses these instead of internal state. */
  value?: string;
  onChange?: (value: string) => void;
  subQuestions?: ProjectResponseComponentProps['subQuestions'];
  notApplicableLabel?: string | null;
}

export default function HandsFreePopupMessageRenderer({
  type,
  text,
  options,
  optionRenderType,
  assets,
  contentUrl,
  onSubmitContent,
  isSubmitting = false,
  awaitingTaskCompletion = false,
  maxSelections,
  isConversationOver = false,
  value: controlledValue,
  onChange,
  subQuestions,
  notApplicableLabel,
}: Props) {
  const t = useTranslations('UserTest');

  const setIframeUrl = useSetIframeUrl();
  const { currentTranscript, isListening } = useProjectResponseModeContext();
  const [localValue, setLocalValue] = useState('');
  const isControlled = controlledValue !== undefined && onChange !== undefined;
  const value = isControlled ? controlledValue : localValue;
  const setValue = isControlled ? onChange : setLocalValue;

  const hasOptions =
    options &&
    options.length > 0 &&
    (type === 'multipleChoice' ||
      type === 'rating' ||
      type === 'ranking' ||
      type === 'matrix');

  const rankingComplete =
    type === 'ranking' &&
    options &&
    value.trim().split(',').filter(Boolean).length === options.length;

  const handleChange = (newValue: string) => {
    if (isSubmitting) return;
    setValue(newValue);
  };

  const handleUrlClick = () => {
    if (contentUrl) setIframeUrl(contentUrl);
  };

  const isUrlQuestion = type === 'url' || Boolean(contentUrl);

  const reorderedOptions = reorderOptionsForMultipleChoice(options ?? [], type);

  // Parent re-keys this component on question id change so no questionId is needed.
  const tentativeValue = useRealtimeVoiceMatch({
    transcript: currentTranscript ?? '',
    options: reorderedOptions.map((o) => ({ label: o.label, value: o.value })),
    maxSelections: maxSelections ?? undefined,
    questionType: getQuestionTypeForVoiceMatch(type),
    enabled:
      (type === 'multipleChoice' || type === 'rating') &&
      Boolean(hasOptions) &&
      Boolean(onSubmitContent) &&
      !isSubmitting &&
      isListening &&
      !value,
    preserveMatchOnDisable: isSubmitting,
  });

  // `effectiveValue` is what the Send button acts on: prefer the user's
  // explicit click, fall back to the live voice highlight so users can
  // submit what they've verbally selected without touching the screen.
  const effectiveValue = value || tentativeValue;

  const handleSubmitClick = () => {
    if (isSubmitting) return;
    if (hasOptions) {
      if (!effectiveValue.trim() || (type === 'ranking' && !rankingComplete))
        return;
      onSubmitContent?.(effectiveValue.trim());
    } else {
      onSubmitContent?.(type === 'openEnded' ? SKIP_SIGNAL : '');
    }
  };

  const showButton = onSubmitContent && !isConversationOver;
  const buttonDisabled =
    isSubmitting ||
    (hasOptions && !effectiveValue.trim()) ||
    (type === 'ranking' && !rankingComplete);

  const rendererProps: ProjectResponseComponentProps = {
    app: 'survey',
    type,
    text,
    value,
    tentativeValue: tentativeValue || undefined,
    onChange: handleChange,
    readonly: !(hasOptions && Boolean(onSubmitContent)),
    optionRenderType: optionRenderType ?? undefined,
    options: reorderedOptions.map((o) => ({
      label: o.label,
      value: o.value,
      assets: o.assets ?? undefined,
    })),
    assets: assets ?? undefined,
    maxSelections: maxSelections ?? undefined,
    audio: undefined,
    isAiVoiceEnabled: true,
    multiSelect: type === 'multipleChoice' && (maxSelections ?? 0) > 1,
    subQuestions,
    notApplicableLabel,
  };

  if (isUrlQuestion) {
    return (
      <div className='flex flex-col gap-4' data-test='handsfree-popup-url-text'>
        <div className='text-base/6 font-semibold text-gray-900'>
          <button
            type='button'
            className='font-inherit cursor-pointer border-0 bg-transparent p-0 text-inherit underline'
            onClick={handleUrlClick}
            aria-label={t('openNewPageAria')}
          >
            {t('redirecting')}
          </button>
        </div>
        {showButton && (
          <HandsFreePopupSendButton
            variant='next'
            onClick={handleSubmitClick}
            disabled={buttonDisabled}
            isSubmitting={isSubmitting}
            awaitingTaskCompletion={awaitingTaskCompletion}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className='flex flex-col gap-4'
      data-test='handsfree-popup-message-renderer'
    >
      {!hasOptions && (
        <div className='prose mb-0 max-w-none prose-headings:text-base prose-p:text-base/6 prose-p:font-semibold prose-p:text-gray-900'>
          <Markdown
            rehypePlugins={[
              rehypeRaw,
              rehypeIndent,
              [rehypeExtLinks, { target: '_blank' }],
            ]}
            remarkPlugins={[remarkGfm]}
            data-test='handsfree-popup-question-text'
          >
            {ensureTripleBackticks(text)}
          </Markdown>
        </div>
      )}

      {!hasOptions && assets && assets.length > 0 && assets[0]?.url && (
        <div className='flex h-60 max-h-60 min-h-0 w-full items-center justify-center overflow-hidden rounded-lg bg-gray-50'>
          <ProjectResponseAssetItem asset={assets[0]} compact />
        </div>
      )}

      {hasOptions && (
        <div
          className='flex flex-col gap-2'
          data-test='handsfree-popup-options'
        >
          <ProjectResponseMessageRenderer type={type} {...rendererProps} />
        </div>
      )}

      {showButton && (
        <HandsFreePopupSendButton
          variant={hasOptions ? 'send' : 'next'}
          onClick={handleSubmitClick}
          disabled={buttonDisabled}
          isSubmitting={isSubmitting}
          awaitingTaskCompletion={awaitingTaskCompletion}
        />
      )}
    </div>
  );
}
